import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import IconButtonMui from '@mui/material/IconButton';

import heartSmallSvg from '../assets/heart-small.svg';

import { ReactComponent as ClubhouseSvg } from '../assets/clubhouse.svg';
import { ReactComponent as HomeSvg } from '../assets/home.svg';
import { ReactComponent as PlaySvg } from '../assets/play.svg';
import { ReactComponent as RoomSvg } from '../assets/room.svg';

export function PrimaryButton ({ Icon, label, onClick, size = 'medium', sx = {} }) {
  return (
    <Button
      onClick={onClick}
      startIcon={Icon || undefined}
      variant="contained"
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '50px',
        color: '#000000',
        fontSize: '1rem',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: '#E0E0E0'
        },
        height: (size === 'large') ? '3.5rem' : undefined,
        width: (size === 'large') ? '12.5rem' : undefined,
        ...sx,
      }}
    >
      {label || ''}
    </Button>
  );
}

export function PrimaryButtonOldUi ({ Icon, label, onClick, size = 'medium', sx = {} }) {
  return (
    <Button
      onClick={onClick}
      startIcon={Icon || undefined}
      variant="contained"
      sx={{
        border: '1px solid rgb(0, 93, 170)',
        fontFamily: 'MabryPro, Arial',
        borderRadius: '3px',
        background: 'rgb(0, 93, 170)',
        color: 'white',
        paddingRight: '16px',
        paddingLeft: '16px',
        fontSize: '14px',
        fontWeight: 200,
        height: '48px',
        lineHeight: '34px'
      }}
    >
      {label || ''}
    </Button>
  );
}

export function IconButton ({ children, onClick, sx = {} }) {
  return (
    <IconButtonMui
      onClick={onClick}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '50%',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: '#E0E0E0'
        },
        height: '2.75rem',
        width: '2.75rem',
        ...sx,
      }}
    >
      {children}      
    </IconButtonMui>
  );
}

export function HomeTooltip ({ home, onClick, sx = {}, yourHome }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<HomeTooltipDot availability={home.availability} size={'small'} />}
      endIcon={yourHome ? <img src={heartSmallSvg} alt='' width={10} /> : undefined}
      variant="contained"
      sx={{
        backgroundColor: '#FFFFFF',
        border: 'none',
        borderRadius: '55px',
        color: '#2D2D2D',
        fontFamily: 'MabryPro',
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: '0.875rem',
        position: 'relative',
        px: 1,
        py: 0.5,
        textTransform: 'none',
        '&:hover': {
          backgroundColor: '#E0E0E0',
          '.tooltip-arrow': {
            backgroundColor: '#E0E0E0',
          }
        },
        '.MuiButton-startIcon': {
          marginLeft: 0,
          marginRight: '0.25rem',
        },
        ...sx,
      }}
    >
      {`${home.price.split(',')[0]}k`}
      <HomeTooltipArrow size={'small'} />
    </Button>
  );
}

function HomeTooltipArrow ({ size }) {
  return (
    <Box className='tooltip-arrow'
      sx={{
        backgroundColor: '#FFFFFF',
        height: size === 'large' ? '0.625rem' : '0.375rem',
        width: size === 'large' ? '0.625rem' : '0.375rem',
        transform: 'translateX(-50%) rotate(45deg)',
        position: 'absolute',
        bottom: size === 'large' ? '-0.3125rem' : '-0.1875rem',
        left: '50%',
        transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      }}
    />
  );
}

function HomeTooltipDot ({ availability = 'AVAILABLE', size }) {
  const getBgColor = () => {
    switch (availability) {
      case 'AVAILABLE': return '#32D74B';
      case 'SOLD': return '#D73232';
      case 'UNDER_CONSTRUCTION': return '#DFC43E';
      case 'UNRELEASED': return '#32D74B';
      default: return '#32D74B';
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: getBgColor(),
        borderRadius: '50%',
        height: size === 'large' ? '0.625rem' : '0.375rem',
        width: size === 'large' ? '0.625rem' : '0.375rem',
      }}
    />
  );
}

export function HomeHotspot ({ home, onClick, selected, sx }) {
  return (
    <IconButtonMui
      onClick={onClick}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '50%',
        boxShadow:"0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
        '&:hover': {
          backgroundColor: '#E0E0E0'
        },
        padding: 0,
        height: '0.875rem',
        width:'0.875rem',
        ...sx,
      }}
    >
      {home && (
        <HomeTooltipDot availability={home.availability} size='small' />
      )}
    </IconButtonMui>
  );
}

export function AmenityHotspot ({ sx }) {
  return (
    <IconButtonMui
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '50%',
        boxShadow:"0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
        '&:hover': {
          backgroundColor: '#FFFFFF'
        },
        cursor: 'auto',
        padding: 0,
        height: '0.875rem',
        width: '0.875rem',
        ...sx,
      }}
    />     
  );
}

export function AmenityTooltip ({ amenity, onClick, sx }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<ClubhouseSvg />}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        color: '#000000',
        fontFamily: 'MabryPro',
        fontSize: '1rem',
        fontWeight: 300,
        px: 1.25,
        py: 2.5,
        textTransform: 'none',
        '&:hover': {
          backgroundColor: '#E0E0E0',
        },
        ...sx,
      }}
      variant="contained"
    >
      {amenity.name}
    </Button>
  );
}

export function PhaseTooltip ({ phase, onClick, sx }) {
  return (
    <Button
      onClick={onClick}
      startIcon={<ClubhouseSvg />}
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        color: '#2D2D2D',
        fontFamily: 'MabryPro',
        fontSize: '1.375rem',
        fontWeight: 300,
        px: 1.5,
        py: 1.5,
        textAlign: 'left',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: '#E0E0E0',
        },
        'span': {
          marginLeft: 0,
          marginRight: 1.5,
        },
        ...sx,
      }}
      variant="contained"
    >
      <Stack>
        <div>{phase.name}</div>
        <Box
          sx={{
            color: '#767676',
            fontSize: '1rem',
          }}
        >
          {phase.status}
        </Box>
      </Stack>
    </Button>
  );
}

const NAV_BUTTON_GROUP_UNSELECTED_STYLES = {
  backgroundColor: '#FFFFFF',
  border: 'none !important',
  borderRadius: '36px',
  height: '2.5rem',
  width: '2.5rem',
  '&:hover': {
    backgroundColor: '#E0E0E0',
  },
  '&:disabled': {
    'svg path': {
      stroke: 'lightgray',
    },
  },
};

const NAV_BUTTON_GROUP_SELECTED_STYLES = {
  ...NAV_BUTTON_GROUP_UNSELECTED_STYLES,
  backgroundColor: '#2D2D2D',
  '&:hover': {
    backgroundColor: '#424242',
  },
  'svg path': {
    stroke: '#FFFFFF',
  },
};

export function NavButtonGroup ({
  dispatch,
  state,
  sx
}) {
  const isHomeExteriorPage = (state.currentScreen === state.SCREENS.HOME_EXTERIOR);
  const isHome2DPage = (state.currentScreen === state.SCREENS.HOME_2D);
  const isHomeVirtualTourPage = (state.currentScreen === state.SCREENS.HOME_VIRTUAl_TOUR);
  const haveVideo = state.selectedHome?.backgroundVideo;

  return (
    <ButtonGroup
      component={Stack}
      direction='row'
      spacing={2}
      variant='contained'
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '36px',
        py: 0.75,
        px: 1.25,
        ...sx
      }}
    >
      <IconButtonMui
        disabled={
          (!state?.selectedHome?.backgroundVideo?.url)
        }
        onClick={() => dispatch({ type: 'play-button-clicked' })}
        sx={
          (isHomeExteriorPage && haveVideo)
            ? NAV_BUTTON_GROUP_SELECTED_STYLES
            : NAV_BUTTON_GROUP_UNSELECTED_STYLES
        }
      >
        <PlaySvg />
      </IconButtonMui>

      <IconButtonMui
        onClick={() => dispatch({ type: 'home-two-d-button-clicked' })}
        sx={
          (isHome2DPage)
          ? NAV_BUTTON_GROUP_SELECTED_STYLES
          : NAV_BUTTON_GROUP_UNSELECTED_STYLES
        }
      >
        <HomeSvg />
      </IconButtonMui>

      <IconButtonMui
        disabled={!state.vtHasInteriorZones}
        onClick={() => dispatch({ type: 'home-interior-button-clicked' })}
        sx={
          isHomeVirtualTourPage
            ? NAV_BUTTON_GROUP_SELECTED_STYLES
            : NAV_BUTTON_GROUP_UNSELECTED_STYLES
        }
      >
        <RoomSvg />
      </IconButtonMui>
    </ButtonGroup>
  );
}
